import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { useEffect, useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Alert, Button, Card, CardBody, Col, Form, FormFeedback, Input, Label, Row } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  useCreateAccountDutyPayment,
  useEditAccountDutyPayment,
} from "hooks/useAccountDutyPayment"
import { useAccountBankAccounts } from "hooks/useAccountBankAccount"
import { useAccounts } from "hooks/useAccountEntity"

const ones = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
]

const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"]

const convertIntegerToWords = num => {
  if (!num) return ""
  if (num < 20) return ones[num]
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ` ${ones[num % 10]}` : "")
  if (num < 1000) {
    return `${ones[Math.floor(num / 100)]} Hundred${num % 100 ? ` ${convertIntegerToWords(num % 100)}` : ""}`
  }
  if (num < 100000) {
    return `${convertIntegerToWords(Math.floor(num / 1000))} Thousand${num % 1000 ? ` ${convertIntegerToWords(num % 1000)}` : ""}`
  }
  if (num < 10000000) {
    return `${convertIntegerToWords(Math.floor(num / 100000))} Lakh${num % 100000 ? ` ${convertIntegerToWords(num % 100000)}` : ""}`
  }
  return `${convertIntegerToWords(Math.floor(num / 10000000))} Crore${num % 10000000 ? ` ${convertIntegerToWords(num % 10000000)}` : ""}`
}

const amountToWords = value => {
  const numeric = Number(value)
  if (!numeric || Number.isNaN(numeric)) return ""

  const taka = Math.floor(numeric)
  const paisa = Math.round((numeric - taka) * 100)
  const takaWords = convertIntegerToWords(taka) || "Zero"

  if (paisa > 0) {
    return `${takaWords} Taka and ${convertIntegerToWords(paisa)} Poisha Only`
  }

  return `${takaWords} Taka Only`
}

const AccountDutyPaymentAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
  isFetchingEdit,
  onSuccess,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountDutyPayment()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccountDutyPayment()

  const { data: bankAccountResponse, isLoading: bankAccountLoading } = useAccountBankAccounts(
    { all: true, per_page: 200 },
    modal
  )
  const { data: accountResponse, isLoading: accountLoading } = useAccounts(
    { all: true, per_page: 200 },
    modal
  )

  const bankAccounts = useMemo(
    () => bankAccountResponse?.data?.data || bankAccountResponse?.data || [],
    [bankAccountResponse]
  )
  const ledgerAccounts = useMemo(
    () => accountResponse?.data?.data || accountResponse?.data || [],
    [accountResponse]
  )

  const validationSchema = Yup.object({
    date: Yup.string().required("Please select a payment date"),
    designation: Yup.string().required("Please enter designation"),
    company_bank_account_id: Yup.mixed().required("Please select company bank account"),
    account_id: Yup.mixed().nullable(),
    bank_name: Yup.string().required("Bank name is required"),
    branch_name: Yup.string().required("Branch name is required"),
    account_name: Yup.string().required("Account name is required"),
    account_number: Yup.string().required("Account number is required"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .min(0.01, "Amount must be at least 0.01")
      .required("Please enter duty amount"),
    amount_in_words: Yup.string().nullable(),
    vehicle_id: Yup.mixed().nullable(),
    chassis_no: Yup.string().required("Please enter chassis number"),
    model_year: Yup.string().nullable(),
    bill_of_entry_registration_no: Yup.string().required("Please enter bill of entry registration no"),
    registration_year: Yup.string().required("Please enter registration year"),
    declarant_code_ain: Yup.string().required("Please enter declarant code (AIN)"),
    customs_office_code: Yup.string().required("Please enter customs office code"),
    status: Yup.string().nullable(),
    notes: Yup.string().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: editedData?.date || new Date().toISOString().slice(0, 10),
      designation: editedData?.designation || "Manager",
      company_bank_account_id:
        editedData?.company_bank_account_id || editedData?.company_bank_account?.id || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      bank_name: editedData?.bank_name || "",
      branch_name: editedData?.branch_name || "",
      account_name: editedData?.account_name || "",
      account_number: editedData?.account_number || "",
      amount: editedData?.amount ?? "",
      amount_in_words: editedData?.amount_in_words || "",
      vehicle_id: editedData?.vehicle_id || editedData?.vehicle?.id || "",
      chassis_no: editedData?.chassis_no || "",
      model_year: editedData?.model_year || "",
      bill_of_entry_registration_no: editedData?.bill_of_entry_registration_no || "",
      registration_year: editedData?.registration_year || new Date().getFullYear().toString(),
      declarant_code_ain: editedData?.declarant_code_ain || "",
      customs_office_code: editedData?.customs_office_code || "",
      status: editedData?.status || "pending",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(
      async (values, { resetForm }) => {
        const payload = {
          date: values.date,
          designation: values.designation.trim() || "Manager",
          company_bank_account_id: values.company_bank_account_id,
          account_id: values.account_id || null,
          bank_name: values.bank_name.trim(),
          branch_name: values.branch_name.trim(),
          account_name: values.account_name.trim(),
          account_number: values.account_number.trim(),
          amount: Number(values.amount),
          amount_in_words: values.amount_in_words?.trim() || null,
          vehicle_id: values.vehicle_id || null,
          chassis_no: values.chassis_no.trim(),
          model_year: values.model_year?.trim() || null,
          bill_of_entry_registration_no: values.bill_of_entry_registration_no.trim(),
          registration_year: values.registration_year.trim(),
          declarant_code_ain: values.declarant_code_ain.trim(),
          customs_office_code: values.customs_office_code.trim(),
          status: values.status || "pending",
          notes: values.notes?.trim() || null,
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        try {
          const res = await action
          toast.success(res?.message ?? `Duty payment ${isEdit ? "updated" : "created"} successfully.`)
          resetForm()
          toggleModal()
          onSuccess?.()
        } catch {
          // axios interceptor shows API errors
        }
      },
      [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess]
    ),
  })

  const applyBankAccount = account => {
    if (!account) return
    formik.setFieldValue("company_bank_account_id", String(account.id))
    formik.setFieldValue("bank_name", account.bank_name || "")
    formik.setFieldValue("branch_name", account.branch_name || "")
    formik.setFieldValue("account_name", account.account_name || "")
    formik.setFieldValue("account_number", account.account_number || "")
  }

  const handleBankAccountChange = e => {
    const id = e.target.value
    const account = bankAccounts.find(item => String(item.id) === String(id))
    if (account) {
      applyBankAccount(account)
    } else {
      formik.setFieldValue("company_bank_account_id", "")
      formik.setFieldValue("bank_name", "")
      formik.setFieldValue("branch_name", "")
      formik.setFieldValue("account_name", "")
      formik.setFieldValue("account_number", "")
    }
  }

  const handleVehicleSelect = vehicle => {
    if (!vehicle) {
      formik.setFieldValue("chassis_no", "")
      return
    }
    formik.setFieldValue("chassis_no", vehicle.full_chassis || vehicle.chassis_number || "")
    formik.setFieldValue(
      "model_year",
      vehicle.year?.name || vehicle.vs_year?.name || vehicle.model_year || ""
    )
  }

  useEffect(() => {
    if (isEdit || !modal || bankAccounts.length === 0 || formik.values.company_bank_account_id) {
      return
    }
    const defaultAccount =
      bankAccounts.find(account => account.is_default && account.is_active !== false) ||
      bankAccounts.find(account => account.is_active !== false)
    if (defaultAccount) {
      applyBankAccount(defaultAccount)
    }
  }, [bankAccounts, isEdit, modal, formik.values.company_bank_account_id])

  useEffect(() => {
    if (isEdit || !modal || ledgerAccounts.length === 0 || formik.values.account_id) {
      return
    }
    formik.setFieldValue("account_id", String(ledgerAccounts[0].id))
  }, [ledgerAccounts, isEdit, modal, formik.values.account_id])

  useEffect(() => {
    const words = amountToWords(formik.values.amount)
    if (words) {
      formik.setFieldValue("amount_in_words", words)
    }
  }, [formik.values.amount])

  const isSubmitDisabled =
    createLoading || editLoading || bankAccountLoading || accountLoading || (isEdit && !!isFetchingEdit)

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Duty Payment" : "New Duty Payment"}
      size="xl"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
          <Alert color="danger" className="mb-3 py-2 px-3">
            <div className="fw-semibold mb-1 small">Please fix the following errors:</div>
            <ul className="mb-0 ps-3">
              {Object.values(formik.errors).map((err, index) => (
                <li key={index} className="small">{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Card className="border mb-3">
          <CardBody>
            <h5 className="mb-3">Date and Bank Information</h5>
            <Row>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="date">Date <span className="text-danger">*</span></Label>
                  <InputField
                    type="date"
                    name="date"
                    id="date"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                    invalid={formik.touched.date && !!formik.errors.date}
                  />
                  {formik.touched.date && formik.errors.date && (
                    <FormFeedback type="invalid">{formik.errors.date}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="designation">Designation</Label>
                  <InputField
                    type="text"
                    name="designation"
                    id="designation"
                    value={formik.values.designation}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="company_bank_account_id">Company Bank Account <span className="text-danger">*</span></Label>
                  <Input
                    type="select"
                    name="company_bank_account_id"
                    id="company_bank_account_id"
                    value={formik.values.company_bank_account_id}
                    onChange={handleBankAccountChange}
                    disabled={bankAccountLoading}
                    invalid={formik.touched.company_bank_account_id && !!formik.errors.company_bank_account_id}
                  >
                    <option value="">Select company bank account</option>
                    {bankAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} - {account.bank_name} - {account.account_number}
                        {account.is_default ? " (default)" : ""}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.company_bank_account_id && formik.errors.company_bank_account_id && (
                    <FormFeedback>{formik.errors.company_bank_account_id}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <InputField type="text" name="bank_name" id="bank_name" value={formik.values.bank_name} onChange={formik.handleChange} />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="branch_name">Branch Name</Label>
                  <InputField type="text" name="branch_name" id="branch_name" value={formik.values.branch_name} onChange={formik.handleChange} />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="account_name">Account Name</Label>
                  <InputField type="text" name="account_name" id="account_name" value={formik.values.account_name} onChange={formik.handleChange} />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="account_number">Account Number</Label>
                  <InputField type="text" name="account_number" id="account_number" value={formik.values.account_number} onChange={formik.handleChange} />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="account_id">Debit Ledger Account</Label>
                  <Input
                    type="select"
                    name="account_id"
                    id="account_id"
                    value={formik.values.account_id}
                    onChange={formik.handleChange}
                    disabled={accountLoading}
                  >
                    <option value="">Select ledger account</option>
                    {ledgerAccounts.map(account => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </Input>
                </div>
              </Col>
              <Col md={6}>
                <StatusButtonGroup
                  name="status"
                  value={formik.values.status}
                  onChange={value => formik.setFieldValue("status", value)}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mb-3">
          <CardBody>
            <h5 className="mb-3">Payment Amount</h5>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount (BDT) <span className="text-danger">*</span></Label>
                  <InputField
                    type="number"
                    step="0.01"
                    min="0.01"
                    name="amount"
                    id="amount"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    invalid={formik.touched.amount && !!formik.errors.amount}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={8}>
                <div className="mb-3">
                  <Label htmlFor="amount_in_words">Amount in Words</Label>
                  <InputField
                    type="text"
                    name="amount_in_words"
                    id="amount_in_words"
                    value={formik.values.amount_in_words}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mb-3">
          <CardBody>
            <h5 className="mb-3">Vehicle and Bill of Entry</h5>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <VehicleIdSelect formik={formik} onVehicleSelect={handleVehicleSelect} />
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="chassis_no">Chassis No <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="chassis_no"
                    id="chassis_no"
                    value={formik.values.chassis_no}
                    onChange={formik.handleChange}
                    invalid={formik.touched.chassis_no && !!formik.errors.chassis_no}
                  />
                  {formik.touched.chassis_no && formik.errors.chassis_no && (
                    <FormFeedback type="invalid">{formik.errors.chassis_no}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="model_year">Model Year</Label>
                  <InputField type="text" name="model_year" id="model_year" value={formik.values.model_year} onChange={formik.handleChange} />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="bill_of_entry_registration_no">Bill of Entry Registration No <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="bill_of_entry_registration_no"
                    id="bill_of_entry_registration_no"
                    value={formik.values.bill_of_entry_registration_no}
                    onChange={formik.handleChange}
                    invalid={formik.touched.bill_of_entry_registration_no && !!formik.errors.bill_of_entry_registration_no}
                  />
                  {formik.touched.bill_of_entry_registration_no && formik.errors.bill_of_entry_registration_no && (
                    <FormFeedback type="invalid">{formik.errors.bill_of_entry_registration_no}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="registration_year">Registration Year <span className="text-danger">*</span></Label>
                  <InputField type="text" name="registration_year" id="registration_year" value={formik.values.registration_year} onChange={formik.handleChange} />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="declarant_code_ain">Declarant Code (AIN) <span className="text-danger">*</span></Label>
                  <InputField type="text" name="declarant_code_ain" id="declarant_code_ain" value={formik.values.declarant_code_ain} onChange={formik.handleChange} />
                </div>
              </Col>
              <Col md={2}>
                <div className="mb-3">
                  <Label htmlFor="customs_office_code">Customs Office Code <span className="text-danger">*</span></Label>
                  <InputField type="text" name="customs_office_code" id="customs_office_code" value={formik.values.customs_office_code} onChange={formik.handleChange} />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mb-3">
          <CardBody>
            <Label htmlFor="notes">Notes</Label>
            <InputField
              type="textarea"
              name="notes"
              id="notes"
              rows={2}
              value={formik.values.notes}
              onChange={formik.handleChange}
            />
          </CardBody>
        </Card>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {isEdit ? "Save Changes" : "Create Duty Payment"}
            {(createLoading || editLoading) ? "..." : ""}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountDutyPaymentAddEdit
